import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import instance from '../api/Axios';

export const usePermissions = () => {
  const { userId, userEmail, userType, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserPermissions = useCallback(async () => {
    if (!isAuthenticated || !userId) {
      console.log('No authenticated user found, skipping permission fetch');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('About to fetch permissions for user:', userEmail);
      
      let response;
      let userPermissions = [];
      
      // Try multiple endpoints to get user permissions
      try {
        console.log('Trying permissions/user_permissions/ endpoint...');
        response = await instance.get('/api/accounts/permissions/user_permissions/');
        console.log('Permissions API response:', response.data);
        
        if (response.data.all_permissions) {
          userPermissions = response.data.all_permissions;
          console.log('Found all_permissions:', userPermissions.length);
        }
      } catch (permError) {
        console.log('Permissions endpoint failed, trying user endpoint...');
        
        try {
          response = await instance.get('/api/accounts/user/');
          console.log('User API response:', response.data);
          
          // Check if user object has permission-related data
          if (response.data.permissions) {
            userPermissions = response.data.permissions;
          } else if (response.data.user_permissions) {
            userPermissions = response.data.user_permissions;
          }
        } catch (userError) {
          console.log('User endpoint also failed, trying user-permissions endpoint...');
          
          try {
            // Try the user-permissions management endpoint 
            response = await instance.get(`/api/accounts/user-permissions/${userId}/`);
            console.log('User-permissions endpoint response:', response.data);
            
            if (response.data.permissions) {
              userPermissions = response.data.permissions;
            }
          } catch (userPermError) {
            console.log('All endpoints failed');
            throw permError; // Throw the original error
          }
        }
      }
      
      console.log('Final permissions found:', userPermissions.length);
      console.log('Permission details:', userPermissions.slice(0, 3));
      
      setPermissions(userPermissions);
      setError(null);
    } catch (err) {
      console.error('Error fetching user permissions:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      console.log('No permissions could be loaded - user has no permissions');
      setPermissions([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userId, userEmail]);

  useEffect(() => {
    console.log('usePermissions useEffect triggered');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('userId:', userId);
    console.log('userEmail:', userEmail);
    console.log('userType:', userType);
    
    fetchUserPermissions();
  }, [fetchUserPermissions, isAuthenticated, userId, userEmail, userType]);

  // Function to manually refresh permissions
  const refreshPermissions = useCallback(() => {
    console.log('Manually refreshing permissions...');
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  // Helper function to check if user has a specific permission
  const hasPermission = (permission) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(perm => 
      perm.codename === permission || 
      perm.name === permission ||
      `${perm.content_type?.app_label}.${perm.codename}` === permission
    );
  };

  // Helper function to check if user has any of the provided permissions
  const hasAnyPermission = (permissionList) => {
    if (!Array.isArray(permissionList)) return false;
    return permissionList.some(permission => hasPermission(permission));
  };

  // Helper function to check if user has all of the provided permissions
  const hasAllPermissions = (permissionList) => {
    if (!Array.isArray(permissionList)) return false;
    return permissionList.every(permission => hasPermission(permission));
  };

  // Helper function to get permissions by category/app
  const getPermissionsByApp = (appLabel) => {
    return permissions.filter(perm => perm.content_type?.app_label === appLabel);
  };

  // Helper function to check user type permissions
  const canManageUserType = (userType) => {
    const userTypePermissions = {
      'student': ['view_user', 'change_user'],
      'accommodation_provider': ['view_user', 'change_user'],
      'primary_representative': ['view_user', 'change_user'],
      'secondary_representative': ['view_user', 'change_user'],
      'bmo_officer': ['view_user', 'change_user'],
      'funder_primary_representative': ['view_funders', 'manage_funders'],
      'funder_secondary_representative': ['view_funders'],
      'funder_officer': ['view_funders'],
    };

    const requiredPerms = userTypePermissions[userType] || [];
    return hasAnyPermission(requiredPerms);
  };

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissionsByApp,
    canManageUserType,
    // Convenience functions for common permission checks - using ACTUAL permission names
    canViewUsers: () => hasPermission('view_user'),
    canManageUsers: () => hasPermission('change_user'),
    canViewFunders: () => hasAnyPermission(['view_funder', 'add_funder', 'change_funder']),
    canManageFunders: () => hasAnyPermission(['change_funder', 'add_funder']),
    canCreateFunders: () => hasPermission('add_funder'),
    canApproveFunders: () => hasPermission('change_funder'),
    canViewBursaries: () => hasAnyPermission(['view_bursary', 'add_bursary', 'change_bursary', 'view_bursaries', 'manage_bursaries']),
    canManageBursaries: () => hasAnyPermission(['change_bursary', 'add_bursary', 'manage_bursaries']),
    canCreateBursaries: () => hasAnyPermission(['add_bursary', 'manage_bursaries']),
    canViewBursaryComponents: () => {
      const result = hasAnyPermission(['view_component', 'add_component', 'change_component', 'view_bursary_components', 'manage_bursary_components', 'view_bursaries', 'manage_bursaries']);
      console.log('canViewBursaryComponents - checking permissions:', ['view_component', 'add_component', 'change_component', 'view_bursary_components', 'manage_bursary_components', 'view_bursaries', 'manage_bursaries']);
      console.log('canViewBursaryComponents - result:', result);
      return result;
    },
    canManageBursaryComponents: () => {
      const result = hasAnyPermission(['add_component', 'change_component', 'delete_component', 'manage_bursary_components', 'manage_bursaries']);
      console.log('canManageBursaryComponents - checking permissions:', ['add_component', 'change_component', 'delete_component', 'manage_bursary_components', 'manage_bursaries']);
      console.log('canManageBursaryComponents - result:', result);
      return result;
    },
    canViewReports: () => hasAnyPermission(['view_funder', 'view_funderpayment', 'view_funderagreement']),
    canManagePermissions: () => hasAnyPermission(['change_user', 'add_permissiontemplate', 'change_permissiontemplate']),
    
    // Unified funder-related permissions - all based on core funder permissions
    canManageFunderOfficers: () => hasAnyPermission(['add_funder', 'change_funder']),
    canManageFunderRepresentatives: () => hasAnyPermission(['add_funder', 'change_funder']),
    
    // Convenience functions for unified access (all based on funder permissions)
    canViewRepresentatives: () => hasAnyPermission(['view_funder', 'add_funder', 'change_funder']),
    canManageRepresentatives: () => hasAnyPermission(['add_funder', 'change_funder']),
    
    canViewFunderApplications: () => hasAnyPermission(['view_funderapplication', 'add_funderapplication', 'change_funderapplication']),
    canManageFunderApplications: () => hasAnyPermission(['change_funderapplication', 'add_funderapplication']),
    canViewFunderAgreements: () => hasAnyPermission(['view_funderagreement', 'add_funderagreement', 'change_funderagreement']),
    canManageFunderAgreements: () => hasAnyPermission(['change_funderagreement', 'add_funderagreement']),
    canViewFunderPayments: () => hasAnyPermission(['view_funderpayment', 'add_funderpayment', 'change_funderpayment']),
    canManageFunderPayments: () => hasAnyPermission(['change_funderpayment', 'add_funderpayment']),
    
    // Booking permissions
    canViewBookings: () => hasAnyPermission(['view_voucherbooking', 'add_voucherbooking', 'change_voucherbooking']),
    canManageBookings: () => hasAnyPermission(['change_voucherbooking', 'add_voucherbooking']),
    
    // Permission refresh function
    refreshPermissions,
  };
};

export default usePermissions;
